// Display-side mirror of the server invite quota model
// (packages/functions/src/services/betaInvites/quota.ts). The two packages don't share
// code, so the closed set + numbers are duplicated here for the "N of M left" display and
// the launcher gate. The callable's returned `remaining` stays authoritative for
// enforcement — this is only for rendering. Keep these values in sync with the server.
export enum InviteCapability {
  Inviter = 'inviter',
  SuperInviter = 'super-inviter'
}

const INVITE_QUOTA_BY_TIER: Record<InviteCapability, number> = {
  [InviteCapability.Inviter]: 10,
  [InviteCapability.SuperInviter]: 100
}

export const INVITE_QUOTA_PERIOD_MS = 7 * 24 * 60 * 60 * 1000

function isInviteCapability(value: string): value is InviteCapability {
  return value === InviteCapability.Inviter || value === InviteCapability.SuperInviter
}

// Whether the user holds any invite tier — gates the "Invite a friend" launcher.
export function canInvite(capabilities: readonly string[]): boolean {
  return capabilities.some(isInviteCapability)
}

// The allowance for a capability set: the max quota across held invite tiers (0 if none).
export function effectiveInviteQuota(capabilities: readonly string[]): number {
  let quota = 0
  for (const capability of capabilities) {
    if (isInviteCapability(capability)) {
      quota = Math.max(quota, INVITE_QUOTA_BY_TIER[capability])
    }
  }
  return quota
}

export interface InviteAllowanceView {
  total: number
  remaining: number
  // ms until the active window resets, or null when no window is active (nothing sent, or
  // the prior window has already elapsed under the lazy reset).
  resetInMs: number | null
}

// Derives the displayable allowance, applying the same lazy fixed-window reset as the
// server: a window that has elapsed (or was never opened) reads as 0 used with no pending
// reset, so the count shows the full grant.
export function deriveInviteAllowance(
  capabilities: readonly string[],
  inviteQuota: { periodStartMs: number; used: number } | null,
  nowMs: number
): InviteAllowanceView {
  const total = effectiveInviteQuota(capabilities)
  const active = inviteQuota !== null && nowMs < inviteQuota.periodStartMs + INVITE_QUOTA_PERIOD_MS
  const used = active ? (inviteQuota as { used: number }).used : 0
  return {
    total,
    remaining: Math.max(0, total - used),
    resetInMs: active ? (inviteQuota as { periodStartMs: number }).periodStartMs + INVITE_QUOTA_PERIOD_MS - nowMs : null
  }
}
